import {createFeatureSelector, createSelector} from '@ngrx/store';
import {
  ClustersState,
  clustersAdapter,
} from './clusters.state';
import {Cluster, ClusterInstance} from '../../services/api/clusters/cluster.model';
import {Stream, StreamConfiguration} from '../../services/api/stream-configurations/stream-configuration.model';
import {getStreamConfigurationsRecord} from '../stream-configurations/load-all/stream-configurations.load-all.selectors';

/**
 * Feature key for clusters
 */
export const clustersFeatureKey = 'clusters';

/**
 * Feature selector for clusters state
 */
export const selectClustersState =
  createFeatureSelector<ClustersState>(clustersFeatureKey);

/**
 * Get entity selectors from adapter
 */
const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal,
} = clustersAdapter.getSelectors(selectClustersState);

// ==================== Entity Selectors ====================

/**
 * Select all cluster IDs
 */
export const selectClusterIds = selectIds;

/**
 * Select clusters as entities (dictionary)
 */
export const selectClusterEntities = selectEntities;

/**
 * Select all clusters as an array
 */
export const selectAllClusters = selectAll;

/**
 * Select the total count of clusters
 */
export const selectClustersTotal = selectTotal;

// ==================== Loading State Selectors ====================

/**
 * Select a loading state for all clusters
 */
export const selectClustersLoading = createSelector(
  selectClustersState,
  (state) => state.loading
);

/**
 * Select loading state for a specific cluster by ID
 */
export const selectClusterLoadingById = (id: string) =>
  createSelector(
    selectClustersState,
    (state) => state.loadingById[id] || false
  );

/**
 * Select creating state
 */
export const selectClusterCreating = createSelector(
  selectClustersState,
  (state) => state.creating
);

/**
 * Select an updating state for a specific cluster by ID
 */
export const selectClusterUpdating = (id: string) =>
  createSelector(
    selectClustersState,
    (state) => state.updating[id] || false
  );

/**
 * Select a deleting state for a specific cluster by ID
 */
export const selectClusterDeleting = (id: string) =>
  createSelector(
    selectClustersState,
    (state) => state.deleting[id] || false
  );

/**
 * Select if any operation is in progress
 */
export const selectClustersAnyLoading = createSelector(
  selectClustersState,
  (state) => {
    const hasLoadingById = Object.values(state.loadingById).some(Boolean);
    const hasUpdating = Object.values(state.updating).some(Boolean);
    const hasDeleting = Object.values(state.deleting).some(Boolean);

    return state.loading || state.creating || hasLoadingById || hasUpdating || hasDeleting || state.regionsLoading;
  }
);

// ==================== Error State Selectors ====================

/**
 * Select general error
 */
export const selectClustersError = createSelector(
  selectClustersState,
  (state) => state.error
);

/**
 * Select create error
 */
export const selectClusterCreateError = createSelector(
  selectClustersState,
  (state) => state.createError
);

/**
 * Select update error for a specific cluster by ID
 */
export const selectClusterUpdateError = (id: string) =>
  createSelector(
    selectClustersState,
    (state) => state.updateError[id] || null
  );

/**
 * Select delete error for a specific cluster by ID
 */
export const selectClusterDeleteError = (id: string) =>
  createSelector(
    selectClustersState,
    (state) => state.deleteError[id] || null
  );

// ==================== Metadata Selectors ====================

/**
 * Select if clusters have been loaded at least once
 */
export const selectClustersLoaded = createSelector(
  selectClustersState,
  (state) => state.loaded
);

/**
 * Select last updated timestamp
 */
export const selectClustersLastUpdated = createSelector(
  selectClustersState,
  (state) => state.lastUpdated
);

/**
 * Select selected cluster ID
 */
export const selectSelectedClusterId = createSelector(
  selectClustersState,
  (state) => state.selectedClusterId
);

/**
 * Select the currently selected cluster entity
 */
export const selectSelectedCluster = createSelector(
  selectClusterEntities,
  selectSelectedClusterId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : null)
);

// ==================== Cluster Regions Selectors ====================

/**
 * Select cluster regions
 */
export const selectClusterRegions = createSelector(
  selectClustersState,
  (state) => state.regions
);

/**
 * Select cluster regions loading state
 */
export const selectClusterRegionsLoading = createSelector(
  selectClustersState,
  (state) => state.regionsLoading
);

/**
 * Select cluster regions loaded state
 */
export const selectClusterRegionsLoaded = createSelector(
  selectClustersState,
  (state) => state.regionsLoaded
);

// ==================== Specific Entity Selectors ====================

/**
 * Select a specific cluster by ID
 */
export const selectClusterById = (id: string) =>
  createSelector(
    selectClusterEntities,
    (entities) => entities[id] || null
  );

/**
 * Select clusters sorted by name alphabetically
 */
export const selectClustersSortedByName = createSelector(
  selectAllClusters,
  (clusters) => [...clusters].sort((a, b) => a.name.localeCompare(b.name))
);

/**
 * Select clusters sorted by creation date (newest first)
 */
export const selectClustersSortedByNewest = createSelector(
  selectAllClusters,
  (clusters: Cluster[]): Cluster[] => [...clusters].sort((a: Cluster, b: Cluster): 1 | -1 => a.createdAt < b.createdAt ? 1 : -1)
);

/**
 * Select active clusters
 */
export const selectActiveClusters = createSelector(
  selectAllClusters,
  (clusters: Cluster[]): Cluster[] => clusters.filter((cluster: Cluster): boolean => cluster.status === 'active')
);

// ==================== Stream Count Selectors ====================

export interface InstanceStreamCount extends ClusterInstance {
  streamCount: number;
}

export interface ClusterStreamCount extends Cluster {
  instances: InstanceStreamCount[];
}

function countInstanceStreams(instanceId: string, streamConfigurations: StreamConfiguration[]): number {
  if (!streamConfigurations) {
    return 0;
  }

  let streamCount: number = 0;

  for (const configuration of streamConfigurations) {
    const streams: Stream[] = configuration.streams;
    const instanceStreams: Stream[] = streams.filter((stream: Stream): boolean => stream.instanceId === instanceId);
    streamCount += instanceStreams.length;
  }

  return streamCount;
}

function transformWithStreamCount(clusters: Cluster[], streamConfigurations: StreamConfiguration[]): ClusterStreamCount[] {
  if (!clusters) {
    return [];
  }

  return clusters
    .map((cluster: Cluster): ClusterStreamCount => {
      const instances: InstanceStreamCount[] = cluster.instances
        .map((instance: ClusterInstance): InstanceStreamCount => {
          const instanceId: string = instance.id;
          const streamCount: number = countInstanceStreams(instanceId, streamConfigurations);
          return {...instance, streamCount: streamCount};
        });

      return {...cluster, instances: instances};
    });
}

/**
 * Select clusters with stream count for each instance
 */
export const selectClustersWithStreamCount = createSelector(
  selectAllClusters,
  getStreamConfigurationsRecord,
  (clusters: Cluster[], streamConfigurations: StreamConfiguration[]): ClusterStreamCount[] =>
    transformWithStreamCount(clusters, streamConfigurations)
);

/**
 * Select active clusters with stream count for each instance
 */
export const selectActiveClustersWithStreamCount = createSelector(
  selectActiveClusters,
  getStreamConfigurationsRecord,
  (clusters: Cluster[], streamConfigurations: StreamConfiguration[]): ClusterStreamCount[] =>
    transformWithStreamCount(clusters, streamConfigurations)
);
